import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactDOM from 'react-dom';

const BASE = 'https://menu.searchisend.in';

const Gallery = ({ token, onSelectImage }) => {
    const [items, setItems] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');
    const [isRequesting, setIsRequesting] = useState(false);
    const [allImages, setAllImages] = useState(null);

    const fetchGallery = async (path = '') => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_gallery.php?action=list&path=${encodeURIComponent(path)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setItems(data.items);
                setCurrentPath(data.current_path);
            }
        } catch (err) {
            console.error('Error fetching gallery', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllImages = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_gallery.php?action=list_all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setAllImages(data.items);
            }
        } catch (err) {
            console.error('Error fetching all images', err);
        }
    };

    useEffect(() => {
        fetchGallery();
        fetchAllImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!searchQuery.trim()) {
            if (currentPath === 'Search Results') {
                fetchGallery('');
            }
            return;
        }

        if (allImages) {
            const query = searchQuery.toLowerCase();
            const results = allImages.filter(img => img.name.toLowerCase().includes(query));
            setItems(results);
            setCurrentPath('Search Results');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, allImages]);

    const handleRequestImage = async () => {
        if (!requestMessage.trim()) {
            alert('Please describe the image you need.');
            return;
        }
        setIsRequesting(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_gallery.php?action=request_image`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message: requestMessage })
            });
            const data = await res.json();
            if (data.success) {
                alert('Your request has been submitted!');
                setShowRequestModal(false);
                setRequestMessage('');
                window.open(data.whatsapp_url, '_blank');
            } else {
                alert(data.message || 'Failed to submit request.');
            }
        } catch (err) {
            alert('An error occurred while submitting.');
        } finally {
            setIsRequesting(false);
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        fetchGallery('');
    };

    const handleFolderClick = (path) => {
        setSearchQuery('');
        fetchGallery(path);
    };

    const handleBackClick = () => {
        if (!currentPath || currentPath === 'Search Results') return;
        const parts = currentPath.split('/');
        parts.pop(); // Remove current folder
        const parentPath = parts.join('/');
        fetchGallery(parentPath);
    };

    return (
        <div className="admin-card">
            {!onSelectImage && (
                <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            )}
            
            <h1 style={{ marginTop: 0 }}>Stock Image Gallery</h1>
            <p style={{ color: 'var(--text-color-secondary)', marginBottom: '20px' }}>Browse the platform's collection of high-quality stock images to use in your menu.</p>

            <div style={{ backgroundColor: 'var(--bg-color)', padding: '20px', borderRadius: 'var(--border-radius-md)', marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '10px', flexGrow: 1 }}>
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search all images... (auto-searches as you type)" 
                        style={{ flexGrow: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }} 
                    />
                    {searchQuery && (
                        <button type="button" onClick={handleClearSearch} style={{ padding: '10px 20px', border: 'none', backgroundColor: '#6c757d', color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            Clear
                        </button>
                    )}
                </div>
                <button type="button" onClick={() => setShowRequestModal(true)} style={{ padding: '10px 20px', border: 'none', backgroundColor: '#17a2b8', color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    <i className="fas fa-paper-plane" style={{ marginRight: '5px' }}></i> Request an Image
                </button>
            </div>

            {/* Breadcrumb */}
            <div style={{ fontWeight: '500', color: 'var(--text-color-secondary)', marginBottom: '20px' }}>
                <strong>Path:</strong> <span style={{ cursor: 'pointer', color: 'var(--primary-orange)' }} onClick={() => handleFolderClick('')}>Root</span>
                {currentPath && currentPath !== 'Search Results' && currentPath.split('/').map((part, index, arr) => {
                    if (!part) return null;
                    const builtPath = arr.slice(0, index + 1).join('/');
                    return (
                        <span key={index}>
                            {' / '}
                            <span style={{ cursor: 'pointer', color: 'var(--primary-orange)' }} onClick={() => handleFolderClick(builtPath)}>{part}</span>
                        </span>
                    );
                })}
                {currentPath === 'Search Results' && <span> / Search Results</span>}
            </div>

            {loading ? (
                <div style={{ minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>Loading gallery...</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '20px', minHeight: '400px', alignContent: 'start' }}>
                    {/* Back Button */}
                    {currentPath && currentPath !== 'Search Results' && (
                        <div onClick={handleBackClick} style={{ textAlign: 'center', cursor: 'pointer', padding: '15px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--border-color)', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{ fontSize: '3em', color: '#6c757d', marginBottom: '10px' }}><i className="fas fa-arrow-left"></i></div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Back</p>
                        </div>
                    )}

                    {items.length === 0 ? (
                        <p style={{ gridColumn: '1 / -1', color: 'var(--text-color-secondary)' }}>No items found.</p>
                    ) : (
                        items.map((item, idx) => (
                            <div 
                                key={idx} 
                                onClick={() => {
                                    if (item.type === 'folder') handleFolderClick(item.path);
                                    else if (item.type === 'file') {
                                        if (onSelectImage) onSelectImage(item.url);
                                        else window.open(item.url, '_blank');
                                    }
                                }}
                                style={{ textAlign: 'center', cursor: 'pointer', padding: '15px', borderRadius: 'var(--border-radius-md)', transition: 'all 0.2s', border: '1px solid var(--border-color)', backgroundColor: '#fff' }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'var(--primary-orange)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                {item.type === 'folder' ? (
                                    <>
                                        <div style={{ fontSize: '4em', color: '#ffc107', lineHeight: '100px' }}><i className="fas fa-folder"></i></div>
                                        <p style={{ margin: 0, fontWeight: '500', wordWrap: 'break-word', fontSize: '0.9em' }}>{item.name}</p>
                                    </>
                                ) : (
                                    <>
                                        <img src={item.url} alt={item.name} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px', marginBottom: '10px' }} />
                                        <p style={{ margin: 0, fontWeight: '500', wordWrap: 'break-word', fontSize: '0.9em' }}>{item.name}</p>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {showRequestModal && ReactDOM.createPortal(
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10001, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
                    <div style={{ width: '90%', maxWidth: '500px', backgroundColor: '#fff', borderRadius: '16px', padding: '30px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
                        <h2 style={{ marginTop: 0, marginBottom: '10px' }}>Request a New Image</h2>
                        <p style={{ color: '#6c757d', marginBottom: '20px' }}>Describe the image you need, and we'll do our best to add it to the gallery.</p>
                        <textarea 
                            value={requestMessage}
                            onChange={(e) => setRequestMessage(e.target.value)}
                            placeholder="e.g., white pasta"
                            style={{ width: '100%', minHeight: '120px', padding: '15px', borderRadius: '8px', border: '1px solid #cbd5e1', marginBottom: '20px', resize: 'vertical', fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button type="button" onClick={() => setShowRequestModal(false)} style={{ padding: '10px 20px', border: 'none', backgroundColor: '#6c757d', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                Cancel
                            </button>
                            <button type="button" onClick={handleRequestImage} disabled={isRequesting} style={{ padding: '10px 20px', border: 'none', backgroundColor: 'var(--primary-orange)', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', opacity: isRequesting ? 0.7 : 1 }}>
                                {isRequesting ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Gallery;
